import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";

import { toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetOwners,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Owner } from "~/v2/api/rest/account";
import {
  patchOwner,
  postFileUpload,
  postOwner,
} from "~/v2/api/rest/entityOnboarding";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import { useCountry } from "../../../../../api/hooks/useCountry";
import {
  DEFAULT_ACCOUNT_OPENER_VALUES,
  type EntityOnboardingHistoryState,
  REQUIRED_ERR_MSG,
} from "../../constants";
import { usePageTimer } from "../../usePageTimer";
import { useUploadID } from "../../useUploadID";
import {
  getDefaultFormValues,
  isAllAsterisks,
  logAmplitudeEvent,
  type Transform,
  zAffiliationsRefinement,
} from "../../util";
import { ACCOUNT_OPENER_FAQ } from "./faq";
import { AccountOpenerForm } from "./form";
import { type AccountOpener, zAccountOpener } from "./schema";

// Backend values transformed to usable frontend values
export const ACCOUNT_OPENER_TRANSFORMS: [
  Transform<Owner, unknown, boolean>,
  Transform<Owner, string[], string>,
  Transform<Owner, unknown, string>,
] = [
  {
    keys: [
      "is_affiliated_exchange_or_finra",
      "is_control_person",
      "is_politically_exposed",
      "immediate_family_exposed",
    ],
    condition: (value) => value == null,
    transform: () => false,
  },
  {
    keys: ["street_address", "controlling_firms"],
    condition: (value) => Array.isArray(value),
    transform: (value) => value.join(", "),
  },
  {
    keys: "ALL_FIELDS",
    condition: (value) => value == null,
    transform: (_empty) => "",
  },
];

export const AccountOpenerPage = () => {
  const history = useHistory<EntityOnboardingHistoryState>();
  const country = useCountry();

  const queryClient = useQueryClient();
  const { account } = useGetTradingAccount();
  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();
  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: account?.id || "",
    options: { enabled: !!account?.id },
  });

  const { accountOpener, legalEntity } = useMemo(
    () => ({
      accountOpener: owners?.find((details) => details.is_legal_account_opener),
      legalEntity: owners?.find((details) => details.is_legal_entity),
    }),
    [owners],
  );
  // If the account opener returned by useGetOwners has a tax residence they do not need to re-submit a tax id
  const isTaxIDRequired = !accountOpener?.country_of_tax_residence;

  const uploadID = useUploadID({
    accountId: account?.id ?? "",
    ownerId: accountOpener?.owner_id ?? "",
  });

  const pageTimer = usePageTimer("entity_onboarding_account_opener");

  const mutation = useMutationRequest(
    async (data: AccountOpener) => {
      try {
        const newOrUpdatedOwner = await (accountOpener
          ? patchOwner({
              type: "account-opener",
              accountId: account?.id ?? "",
              ownerId: accountOpener.owner_id,
              data,
            })
          : postOwner({
              type: "account-opener",
              accountId: account?.id ?? "",
              data,
            }));

        if (!newOrUpdatedOwner) {
          throw new Error(
            `Failed to ${accountOpener ? "update" : "create"} Account Opener`,
          );
        }
        // Build the list of ID uploads: passport uses a single "passport" sub type,
        // other ID types use "front" and "back" sub types.
        const uploads =
          uploadID.idType === "passport"
            ? [{ subType: "passport" as const, file: uploadID.id.front }]
            : [
                { subType: "front" as const, file: uploadID.id.front },
                { subType: "back" as const, file: uploadID.id.back },
              ];

        await Promise.all(
          uploads
            .filter(({ file }) => file instanceof File)
            .map(async ({ subType, file }) => {
              const res = await postFileUpload({
                accountId: account?.id ?? "",
                ownerId: newOrUpdatedOwner?.owner_id ?? "",
                docType: "identity_verification",
                docSubType: subType,
                file: file as File,
              });
              if (!res?.id) {
                throw new Error(
                  `Failed to upload ${subType === "passport" ? "passport" : `${subType} of ID`}`,
                );
              }
            }),
        );
      } catch (err) {
        const errorMsg = getErrorMessage(err as Error);
        throw new Error(errorMsg);
      }
    },
    {
      onSuccess: () => {
        const duration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(duration);

        // Invalidate the owners query to get the latest account opener
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.owners(account?.id ?? ""),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });

        history.push(Path.ROUTE_ENTITY_ONBOARDING_AUTH_INDIVIDUALS_UBOS, {
          isInEntityOnboarding: true,
        });
      },
      onError: (error) => {
        toastError(
          `Error ${accountOpener ? "updating" : "creating"} Account Opener: ${
            error.message
          }`,
        );
      },
    },
  );

  const form = useForm<AccountOpener>({
    resolver: zodResolver(
      zAccountOpener.superRefine(
        zAffiliationsRefinement({ country, isTaxIDRequired }),
      ),
    ),
    mode: "onChange",
    defaultValues: getDefaultFormValues(
      accountOpener,
      ACCOUNT_OPENER_TRANSFORMS as Transform<Owner>[],
      DEFAULT_ACCOUNT_OPENER_VALUES,
    ),
  });

  const onBackClicked = () => {
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_ENTITY_ONBOARDING_BUSINESS_INFO, {
      isInEntityOnboarding: true,
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<AccountOpener> = (errs) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errs)
      .map((e) => e.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  const onSubmit = ({
    country_of_citizenship,
    country_of_tax_residence,
    tax_id,
    ...values
  }: AccountOpener) => {
    const isFrontIDRequirementMet = uploadID.imageRequirement.includes("front")
      ? !!uploadID.id.front
      : true;
    const isBackIDRequirementMet = uploadID.imageRequirement.includes("back")
      ? !!uploadID.id.back
      : true;

    if (!isFrontIDRequirementMet) {
      uploadID.setError("front", REQUIRED_ERR_MSG);
    }
    if (!isBackIDRequirementMet) {
      uploadID.setError("back", REQUIRED_ERR_MSG);
    }

    if (!isFrontIDRequirementMet || !isBackIDRequirementMet) {
      toastError("ID image(s) are required");
      return;
    }

    const countryOfCitizenshipAlpha3 = country.nameToAlpha3(
      country_of_citizenship,
    );
    const countryOfTaxResidenceAlpha3 = country.nameToAlpha3(
      country_of_tax_residence,
    );

    if (!countryOfCitizenshipAlpha3 || !countryOfTaxResidenceAlpha3) {
      toastError("Please select country from dropdown");
      return;
    }

    const isTaxIDIncluded = isTaxIDRequired || !isAllAsterisks(tax_id ?? "");

    logAmplitudeEvent.nextClicked();
    mutation.mutate({
      ...values,
      country_of_citizenship: countryOfCitizenshipAlpha3,
      country_of_tax_residence: countryOfTaxResidenceAlpha3,
      ...(isTaxIDIncluded ? { tax_id } : {}),
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: country.alpha3ToName is a method on the per-render object returned by the country hook, so listing it would reset the form on every render and discard the user's input. country.isLoading is already listed as the signal that the country data settled.
  useEffect(() => {
    if (accountOpener && !country.isLoading) {
      const additionalTransforms: Transform<
        Owner,
        string | undefined,
        string | undefined
      >[] = [
        {
          keys: ["country_of_citizenship", "country_of_tax_residence"],
          condition: () => true,
          transform: (value) => country.alpha3ToName(value || ""),
        },
      ];

      form.reset({
        ...getDefaultFormValues(
          accountOpener,
          [
            ...ACCOUNT_OPENER_TRANSFORMS,
            ...additionalTransforms,
          ] as Transform<Owner>[],
          DEFAULT_ACCOUNT_OPENER_VALUES,
        ),
        // Only add fake tax id if they are updating account opener
        ...(accountOpener?.owner_id && accountOpener?.country_of_tax_residence
          ? { tax_id: "*********" }
          : {}),
      });
    }
  }, [country.isLoading, accountOpener, form.reset]);

  // Redirect if not entity account
  // In some cases the account type is in limbo updating.
  // Check history before redirecting
  if (
    account &&
    !isLegalEntity &&
    !isLegalEntityLoading &&
    !history?.location?.state?.isInEntityOnboarding
  ) {
    history.replace(Path.ROUTE_NEW_ACCOUNT);
  }

  // If legal entity does not exist, and we have owners,
  // then the user needs to complete previous step (create legal entity)
  if (!isOwnersLoading && owners && owners.length >= 1 && !legalEntity) {
    history.replace(Path.ROUTE_ENTITY_ONBOARDING_BUSINESS_INFO, {
      isInEntityOnboarding: true,
    });
  }

  const isLoading = !account || isOwnersLoading;
  const isSubmitDisabled = mutation.isPending;

  return (
    <OnboardingLayout
      title="Account Opener Information"
      percentage={33}
      isLoading={isLoading}
      faq={ACCOUNT_OPENER_FAQ}
    >
      <AccountOpenerForm
        form={form}
        onBackClicked={onBackClicked}
        isSubmitDisabled={isSubmitDisabled}
        onSubmit={onSubmit}
        onSubmitError={onSubmitInvalid}
        country={country}
        uploadID={uploadID}
        isTaxIDRequired={isTaxIDRequired}
      />
    </OnboardingLayout>
  );
};

export default AccountOpenerPage;
